// Default CMS content for Home & About pages.
// Every string here can be overridden from Admin → Site Settings → Home / About tabs.
// The `useCms(...)` helper reads from settings.home_content / about_content and
// falls back to these defaults when a key is missing.

export const HOME_DEFAULTS = {
  hero_overline: "Precision · Pharma · Talent",
  hero_title_1: "Pharmaco Hires.",
  hero_title_2: "Powerful Partnerships.",
  hero_subtitle:
    "Executive search and talent acquisition for the pharmaceutical and healthcare ecosystem — bridging exceptional talent with organisations pursuing sustainable growth.",
  hero_cta_find_job: "Find a Job",
  hero_cta_hire_talent: "Hire Pharma Talent",
  hero_image: "/images/hero-scientist.jpg",

  bridge_overline: "Bridging the Complexity",
  bridge_title:
    "A steady bridge between exceptional talent and organisational growth.",
  bridge_body:
    "The pharmaceutical, biotechnology, medical device and healthcare industries demand highly skilled, quality-driven professionals. We bridge the gap between exceptional talent and organisations seeking the right candidates to support their growth and business objectives.",

  industries_overline: "Pharma Sub-sectors We Serve",
  industries_title: "Deep functional expertise across the value chain",

  services_overline: "What We Do",
  services_title: "Hiring services built for pharma",

  ecosystem_overline: "Mapping the Pharma Talent Ecosystem",
  ecosystem_title:
    "Customised recruitment aligned to the life-sciences value chain",
  ecosystem_subtitle:
    "Three interlocking domains, one seamless execution — powered by Pharmaco Hires.",
  ecosystem_footer_badge: "Pharmaco Hires Execution",
  ecosystem_footer_body:
    "Every domain rolls up into a single, disciplined execution engine — so your hiring outcomes stay coherent, measurable and fast.",

  pull_quote:
    "We don't just fill vacancies — we connect the right talent with the right opportunities.",
  pull_quote_author: "The Pharmaco Hires Promise",

  featured_jobs_overline: "Current Openings",
  featured_jobs_title: "Featured pharma roles",

  testimonials_overline: "Trusted Partner",
  testimonials_title: "What pharma leaders say about working with us",
  testimonials_body:
    "A blend of scientific depth and recruiter discipline — that's why pharma teams bring us back, role after role.",
  testimonials_image: "/images/business-handshake.jpg",

  strategic_overline: "The Strategic Advantage",
  strategic_title: "Why specialised beats generalist — every time",
  strategic_body:
    "Deep pharma network, precision vetting and inside-out industry knowledge — the reason we're brought back for role after role.",

  faq_overline: "Common Questions",
  faq_title: "Frequently asked questions",
  faq_body:
    "Still curious? Talk to us.",

  final_cta_overline: "Advancing Your Organizational Potential",
  final_cta_title: "Ready to build your pharma team?",
  final_cta_body:
    "Whether you're an employer seeking elite professionals or a candidate preparing for your next career milestone — Pharmaco Hires is your reliable, ethical and efficient recruitment partner.",
};

// ---- Services page ----
export const SERVICES_DEFAULTS = {
  hero_overline: "Our Services",
  hero_title: "Specialist recruitment across every pharma function.",
  hero_body:
    "From Quality Assurance and R&D to Regulatory Affairs, Manufacturing, Marketing, HR and EHS — we recruit across the pharma value chain, at every management level.",

  solutions_overline: "Hiring Solutions",
  solutions_title: "Recruitment across every management level",
  solutions_body:
    "Each mandate is handled by pharma-native recruiters who understand the roles, the market, and the calibre of talent required at every level of the organisation.",
  solutions_cta_label: "Talk to a recruiter",

  domains_overline: "Functional Domains We Recruit For",
  domains_title: "Deep expertise across the pharma value chain",
  domains_body:
    "Whether you're building a QA team, hiring a formulation scientist, or setting up a regulatory function for a new market — we bring domain-mapped recruiters who speak the language.",
  domains_footer:
    "Can't see your department? Talk to us — we cover every corner of pharma.",

  final_cta_title: "Ready to hire the right pharma talent?",
  final_cta_body:
    "Share your hiring brief — we'll respond with a calibrated shortlist within 5-7 business days.",
};

export const ABOUT_DEFAULTS = {
  hero_overline: "About Us",
  hero_title:
    "Connecting exceptional talent with the organisations that need them.",
  hero_body:
    "Pharmaco Hires is a specialised pharma recruitment consultancy — trusted by pharmaceutical, biotechnology, healthcare, medical devices and life-sciences companies across India.",
  hero_image: "/images/business-handshake.jpg",

  story_overline: "Our Story",
  story_title:
    "We don't just fill vacancies — we connect the right talent with the right opportunities.",
  story_body: [
    "Welcome to **Pharmaco Hires**, a specialised pharma recruitment consultancy dedicated to connecting talented professionals with leading pharmaceutical, biotechnology, healthcare, medical devices, and life sciences organisations across India.",
    "We understand that the pharmaceutical and healthcare industries require highly skilled, experienced, and quality-driven professionals. Our mission is to bridge the gap between exceptional talent and organisations seeking the right candidates to support their growth and business objectives.",
    "As a trusted pharma recruitment agency, we provide customised talent acquisition, executive search, and recruitment solutions tailored to the unique hiring needs of pharmaceutical and healthcare companies. Our industry-focused approach enables us to identify, attract, and place top talent across various levels and functions.",
    "Whether you are an employer seeking qualified professionals or a candidate looking for your next career opportunity, Pharmaco Hires is your reliable recruitment partner — committed to delivering timely, efficient, and ethical hiring solutions.",
    "We believe in building long-term relationships with clients and candidates through **professionalism, integrity, transparency, and a commitment to excellence.**",
  ].join("\n\n"),
  story_image: "/images/pharma-team.jpg",

  drives_overline: "What Drives Us",
  drives_title: "Our Vision, Mission & Promise",

  vision_title:
    "To be the most trusted pharma recruitment partner in the industry.",
  vision_body:
    "Building an ecosystem where exceptional pharmaceutical talent and quality-driven organisations find each other seamlessly — powered by domain depth, integrity, and precision.",
  mission_title:
    "Empowering life-sciences organisations with exceptional talent.",
  mission_body:
    "To connect pharmaceutical, biotechnology, healthcare, and life-sciences companies with skilled, quality-oriented professionals through trusted, transparent, and outcome-focused recruitment solutions that fuel business growth and long-term career success.",
  promise_title:
    "Preferred partner for quality-oriented organisations.",
  promise_body:
    "Every mandate we accept is handled with professionalism, confidentiality and precision. We commit to shortlists that respect your quality standards, timelines that respect your business, and relationships built to last well beyond the offer letter.",

  founder_overline: "Meet Our Founder",
  founder_name: "Aishwarya Sawant",
  founder_role: "Founder · Pharmaco Hires",
  founder_image: "",
  founder_credential: "PGDMM, IIMM",
  founder_bio: [
    "Aishwarya Sawant is the Founder of **Pharmaco Hires**, a specialised pharmaceutical recruitment consultancy committed to connecting exceptional talent with leading pharmaceutical, biotechnology, healthcare and life-sciences organisations.",
    "With a passion for connecting exceptional professionals with industry-leading organisations, Aishwarya delivers strategic hiring solutions that drive business growth and career success. With over **33 years of professional experience**, she has developed extensive expertise in pharmaceutical operations and technical recruitment support.",
    "Aishwarya has also served in a **Quality Assurance Management** role, providing executive and technical support while working closely with senior leadership across critical quality functions — giving her a rare, hands-on understanding of what pharma leaders actually need in every hire.",
    "She holds a **Post Graduate Diploma in Materials Management (PGDMM)** from the **Indian Institute of Materials Management (IIMM)**, combining strong management knowledge with decades of practical industry experience.",
  ].join("\n\n"),
  founder_badges: "33+ yrs Pharma Experience, QA Management Background, PGDMM · IIMM",
  founder_quote:
    "Driven by a commitment to quality, integrity, confidentiality and precision, I established Pharmaco Hires to bridge the gap between outstanding pharmaceutical talent and organisations seeking skilled professionals.",

  why_us_overline: "Why Choose Us",
  why_us_title: "Six reasons pharma teams partner with Pharmaco Hires",
  why_us_body:
    "Domain depth, disciplined process, and long-term relationships — the three ingredients that consistently deliver the right hire.",

  why_us_1_title: "Industry Expertise",
  why_us_1_tagline: "We understand the pharmaceutical industry inside out.",
  why_us_1_text:
    "Specialised knowledge that enables us to identify and connect organisations with qualified professionals who meet both technical and cultural requirements.",
  why_us_2_title: "Quality Talent Network",
  why_us_2_tagline: "Connecting the right talent with the right opportunity.",
  why_us_2_text:
    "A strong network of skilled pharmaceutical professionals across functions, ensuring timely access to high-quality candidates.",
  why_us_3_title: "Commitment & Reliability",
  why_us_3_tagline: "Dedicated to delivering results with integrity.",
  why_us_3_text:
    "We work closely with clients and candidates, maintaining transparency, professionalism, and trust throughout the recruitment process.",
  why_us_4_title: "Speed with Precision",
  why_us_4_tagline: "Timely hiring without compromising quality.",
  why_us_4_text:
    "Our streamlined recruitment process helps organisations fill critical positions efficiently while ensuring the best talent fit.",
  why_us_5_title: "Client-Centric Approach",
  why_us_5_tagline: "Every hiring need is unique, and so is our solution.",
  why_us_5_text:
    "Customised recruitment strategies tailored to the specific requirements and business goals of each client.",
  why_us_6_title: "Long-Term Partnerships",
  why_us_6_tagline: "Building relationships beyond placements.",
  why_us_6_text:
    "We believe in creating lasting partnerships by supporting both employers and candidates for sustainable career and business growth.",
};

/**
 * Read a CMS value with fallback.
 *   const val = pick(settings?.home_content, HOME_DEFAULTS, "hero_title_1");
 */
export function pick(source, defaults, key) {
  const v = source && source[key];
  if (v === undefined || v === null || v === "") return defaults[key];
  return v;
}

/**
 * Convert markdown-like **bold** in plain text to a React fragment of <strong>.
 * Used for the hand-written body paragraphs (About story, founder bio, etc.).
 */
export function renderInlineBold(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

/**
 * Convert admin-authored content (with optional **bold** or raw HTML like <a>)
 * into an array of paragraph JSX nodes. Each blank-line-separated block
 * becomes one paragraph. The content is treated as trusted HTML because only
 * authenticated admins can save it (via JWT-guarded PUT /admin/settings), so
 * they may paste <a href="...">, <br>, <strong>, etc.
 */
export function renderRichParagraphs(raw, className = "") {
  if (!raw || !String(raw).trim()) return [];
  const html = String(raw)
    // Backward-compat: convert legacy **bold** markers to <strong>
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  return html
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((body, i) => (
      <p
        key={i}
        className={className}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: body }}
      />
    ));
}

/* -------------------------------------------------------------------- *
 * Hire Talent (Employer) page CMS defaults
 * -------------------------------------------------------------------- */
export const HIRE_TALENT_DEFAULTS = {
  hero_overline: "For Employers",
  hero_title: "Hire pre-vetted pharma talent — faster.",
  hero_description:
    "Share your requirement and our specialist recruiters will get back with a curated shortlist. No spam. No junk profiles. Just people who fit.",

  // 4 value-prop cards
  vp_1_icon: "Target",
  vp_1_title: "Precision-matched shortlists",
  vp_1_body:
    "Every candidate is pre-vetted against role, function, therapy area and cultural fit — not just keywords.",
  vp_2_icon: "Clock",
  vp_2_title: "Fast turnaround",
  vp_2_body:
    "First shortlist within 5-7 working days for most niche pharma / biotech roles.",
  vp_3_icon: "ShieldCheck",
  vp_3_title: "Confidentiality & replacement",
  vp_3_body:
    "Discreet outreach, NDA-protected search and a replacement guarantee within the offer letter window.",
  vp_4_icon: "Users",
  vp_4_title: "Deep pharma network",
  vp_4_body:
    "20,000+ pre-screened professionals across R&D, QA, RA, Manufacturing, Commercial and Medical Affairs.",

  // Trust rail (left column)
  trust_overline: "Why us",
  trust_title: "India's specialist pharma & life-sciences recruiter.",
  trust_bullet_1: "Senior, middle and entry-level hiring across every pharma function.",
  trust_bullet_2: "Domain-trained recruiters — ex-industry, not generalists.",
  trust_bullet_3: "SLA-backed shortlist turnaround with continuous status updates.",
  trust_bullet_4: "Confidential search, NDA-protected engagements.",
  trust_bullet_5: "Track record of 500+ placements across India and GCC.",
  trust_prefer_title: "Prefer to talk?",

  // Form section headings
  employer_section_title: "Employer information",
  employer_section_helper: "Tell us about your organisation. Fields marked with * are required.",
  requirement_section_title: "Required manpower information",
  requirement_section_helper: "Give us as much detail as you can — better inputs mean sharper shortlists.",

  // Submit
  submit_button_text: "Submit requirement",
  submit_footer_note: "We respect your confidentiality — details are shared only with our internal search team.",
  submit_success_toast: "Requirement received. A senior recruiter will reach out within 1 business day.",
};
